import React, { useState } from 'react';
import styled from 'styled-components';
import VipIcon from '../Icons/VipIcons';
import { ConfirmModal } from './Modal';

const TooltipWrapper = styled.div`
    position: relative;
    display: inline-flex;
    align-items: center;
    cursor: ${props => props.disabled ? 'default' : 'pointer'};
`;

const IconWrapper = styled.div`
    display: inline-flex;
    align-items: center;
    color: ${props => props.$iconColor || '#666'};
    transition: color 0.2s ease;
    
    &:hover {
        color: ${props => props.theme?.colors?.primary || '#007bff'};
    }
`;

/**
 * Tooltip Component
 * 
 * A reusable tooltip component that displays helpful information in a modal on click.
 * Uses the common ConfirmModal for consistent styling across the app.
 * 
 * @param {string|React.ReactNode} content - The content to display in the modal
 * @param {string} title - Title for the modal header (default: 'Info')
 * @param {string} iconType - Type of icon to display: 'InfoIcon', 'HelpIcon', etc. (default: 'InfoIcon')
 * @param {number} iconSize - Size of the icon in pixels (default: 18)
 * @param {string} iconColor - Color of the icon (default: '#666')
 * @param {boolean} disabled - Disable tooltip interaction (default: false)
 * @param {string} confirmText - Text for the confirm button (default: 'Got it')
 * @param {React.ReactNode} children - Custom trigger element (if not using default icon)
 */
const Tooltip = ({ 
    content, 
    title = 'Info',
    iconType = 'InfoIcon',
    iconSize = 18,
    iconColor = '#666',
    disabled = false,
    confirmText = 'Got it',
    children,
    // Deprecated props kept for backward compatibility
    position,   // eslint-disable-line no-unused-vars
    width,      // eslint-disable-line no-unused-vars
    maxWidth    // eslint-disable-line no-unused-vars
}) => {
    const [isOpen, setIsOpen] = useState(false);

    const handleOpen = (e) => {
        e.stopPropagation();
        if (disabled) return;
        setIsOpen(true);
    };

    const handleClose = () => {
        setIsOpen(false);
    };

    return (
        <>
            <TooltipWrapper 
                disabled={disabled}
                onClick={handleOpen}
                title="Click for info"
            >
                {children || (
                    <IconWrapper $iconColor={iconColor}>
                        <VipIcon name={iconType} size={iconSize} />
                    </IconWrapper>
                )}
            </TooltipWrapper>
            
            <ConfirmModal
                isOpen={isOpen}
                onClose={handleClose}
                onConfirm={handleClose}
                title={title}
                message={content}
                confirmText={confirmText}
                hideCancel
            />
        </>
    );
};

export default Tooltip;
